/**
 * Production column definitions for the Sales Order DataTable.
 *
 * Maps real Order fields to the column rendering. Reuses PopOutEvents and
 * EventRow from ./PopOutEvents, plus the shared StatusBadge primitive +
 * ORDER_STATUS_BADGE_CONFIG.
 */

import { type MouseEvent } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { PopOutCell } from '../DataTable/PopOutCell';
import { ROW_ACTIONS_MENU_PROPS } from '../DataTable/constants';
import { UserCell } from '../UserCell';
import { PopOutEvents, EventRow } from './PopOutEvents';
import { OrderActionsDropdown } from '../OrderDetails/OrderActionsDropdown';
import type { CurrentUser } from '@/react/lib/capabilities';
import { StatusBadge } from '@/components/ui/status-badge';
import { ORDER_STATUS_BADGE_CONFIG } from '../OrderDetails/order-status-config';
import {
  XERO_INVOICE_BADGE_CONFIG,
  xeroInvoiceState,
  xeroInvoiceUrl,
} from '../OrderDetails/xero-invoice-config';
import type { Order } from '@api/orders';
import { SALES_ORDER_COLUMN_WIDTHS } from '../DataTable/columnWidths';
import { formatCurrency } from '../../utils/formatters';
import { UNDERLINE_SLIDE_IN } from '@/components/ui/link-underline';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function truncate(str: string, maxLength: number): string {
  return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
}


// ─────────────────────────────────────────────────────────────────────────────
// Column factory for the real Order type
// ─────────────────────────────────────────────────────────────────────────────

export interface SalesOrderColumnsOptions {
  onOrderIdClick?: (orderId: number) => void;
  // Signed-in user — gates the row action menu (Edit/Generate vs History).
  currentUser?: CurrentUser;
}

export function getSalesOrderColumns({ onOrderIdClick, currentUser }: SalesOrderColumnsOptions = {}): ColumnDef<Order>[] {
  return [
    {
      accessorKey: 'order_number',
      header: 'Order ID',
      enableSorting: false,
      cell: ({ row }) => {
        const referenceNumber = row.original.reference_number;
        if (onOrderIdClick) {
          return (
            <button
              type="button"
              className={`${UNDERLINE_SLIDE_IN} font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap`}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onOrderIdClick(row.original.id);
              }}
            >
              {referenceNumber}
            </button>
          );
        }
        return (
          <span className={`${UNDERLINE_SLIDE_IN} font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap`}>
            {referenceNumber}
          </span>
        );
      },
      size: SALES_ORDER_COLUMN_WIDTHS.orderId,
    },
    {
      accessorKey: 'events',
      header: 'Events',
      enableSorting: false,
      cell: ({ row }) => {
        const events = row.original.events;
        if (!events || events.length === 0) return <span className="text-grey">-</span>;

        const handleClick = onOrderIdClick ? () => onOrderIdClick(row.original.id) : undefined;

        if (events.length <= 2) {
          return (
            <div className="flex flex-col gap-1.5">
              {events.map((event) => (
                <EventRow key={event.event_id} event={event} onClick={handleClick} />
              ))}
            </div>
          );
        }

        return <PopOutEvents events={events} onEventClick={handleClick} />;
      },
      size: SALES_ORDER_COLUMN_WIDTHS.events,
    },
    {
      accessorKey: 'contact',
      header: 'Customer',
      cell: ({ row }) => {
        const contact = row.original.contact;
        if (!contact) return <span className="text-grey">-</span>;
        const details = [contact.full_name, contact.phone, contact.organisation_name]
          .filter(Boolean)
          .join(' · ');
        const isTruncated = details.length > 50;

        const cellContent = (
          <div className="flex flex-col gap-1.5">
            <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black">
              {contact.email}
            </span>
            {details && (
              <span className="font-inter text-xs leading-[1.25] text-off-black whitespace-nowrap">
                {truncate(details, 50)}
              </span>
            )}
          </div>
        );

        if (!isTruncated) return cellContent;

        return (
          <PopOutCell
            minWidth={SALES_ORDER_COLUMN_WIDTHS.customer}
            collapsed={cellContent}
            expanded={
              <div className="flex flex-col gap-1.5">
                <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black">
                  {contact.email}
                </span>
                <span className="font-inter text-xs leading-[1.25] text-off-black">
                  {details}
                </span>
              </div>
            }
          />
        );
      },
      enableSorting: false,
      size: SALES_ORDER_COLUMN_WIDTHS.customer,
    },
    {
      accessorKey: 'salesperson',
      header: 'Salesperson',
      cell: ({ row }) => (
        <UserCell
          user={row.original.salesperson}
          truncateAfter={14}
          popoutMinWidth={SALES_ORDER_COLUMN_WIDTHS.salesperson}
        />
      ),
      enableSorting: false,
      size: SALES_ORDER_COLUMN_WIDTHS.salesperson,
    },
    {
      accessorKey: 'total_amount',
      header: 'Order value',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-inter text-sm leading-[1.5] text-off-black whitespace-nowrap">
          {formatCurrency(row.original.total_amount)}
        </span>
      ),
      size: SALES_ORDER_COLUMN_WIDTHS.orderValue,
    },
    {
      accessorKey: 'order_status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge
          value={row.original.order_status}
          config={ORDER_STATUS_BADGE_CONFIG}
          className="w-status-badge"
        />
      ),
      enableSorting: false,
      size: SALES_ORDER_COLUMN_WIDTHS.status,
    },
    {
      accessorKey: 'xero_invoice_id',
      header: 'Xero invoice',
      enableSorting: false,
      cell: ({ row }) => {
        const xeroInvoiceId = row.original.xero_invoice_id;
        const badge = (
          <StatusBadge
            value={xeroInvoiceState(xeroInvoiceId)}
            config={XERO_INVOICE_BADGE_CONFIG}
            className="w-status-badge"
          />
        );

        if (!xeroInvoiceId) return badge;

        return (
          <a
            href={xeroInvoiceUrl(xeroInvoiceId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: MouseEvent) => e.stopPropagation()}
            className="inline-flex"
          >
            {badge}
          </a>
        );
      },
      size: SALES_ORDER_COLUMN_WIDTHS.xeroInvoice,
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) => (
        <OrderActionsDropdown
          orderId={row.original.id}
          draftEvents={row.original.draft_events}
          hasPublishableEvents={row.original.has_publishable_events}
          orderStatus={row.original.order_status}
          xeroInvoiceId={row.original.xero_invoice_id}
          canGenerateDocumentsDirectly={row.original.documents_directly_generatable}
          currentUser={currentUser}
          {...ROW_ACTIONS_MENU_PROPS}
        />
      ),
      enableSorting: false,
    },
  ];
}
